/**
 * Shared display formatters for consistent data rendering across the app.
 */

/** Format height for display — normalizes to "6'4\"" style */
export function formatHeight(h: string | null): string | null {
  if (!h) return null
  // Already in correct format like "6'4\""
  if (h.includes("'")) return h
  // Handle "76 in" or "76" (inches) — convert to feet'inches"
  const inches = parseInt(h, 10)
  if (!isNaN(inches) && inches > 12) {
    const feet = Math.floor(inches / 12)
    const remaining = inches % 12
    return `${feet}'${remaining}"`
  }
  return h
}

/** Format weight for display — normalizes to "250 lbs" style */
export function formatWeight(w: string | null): string | null {
  if (!w) return null
  const trimmed = w.trim()
  if (trimmed.toLowerCase().includes('lbs')) return trimmed
  // Pure number — append "lbs"
  const num = parseInt(trimmed, 10)
  if (!isNaN(num)) return `${num} lbs`
  return trimmed
}

/** Capitalize first letter of a string. Returns '—' for null/undefined/empty. */
export function capitalize(s: string | null | undefined): string {
  if (!s) return '—'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Capitalize each word, replacing underscores with spaces. */
export function capitalizeWords(s: string | null | undefined): string {
  if (!s) return '—'
  return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/** Calculate age from date of birth string. Returns null if invalid. */
export function calculateAge(dob: string | null): number | null {
  if (!dob) return null
  const birthDate = new Date(dob)
  if (isNaN(birthDate.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

/**
 * Format a date-only value (due dates, contract dates) as "11 Jul 2026".
 *
 * PocketBase stores these as UTC midnight. Rendering them with
 * toLocaleDateString shifts them to the previous day in any timezone behind
 * UTC — a due date of 11 Jul then displays as 10 Jul while the calendar, which
 * places events by their stored day, shows the 11th. Formatting from the UTC
 * parts keeps every screen agreeing with the stored value.
 *
 * Use this for date-only fields. For real timestamps (when something happened),
 * local time is correct and this is the wrong helper.
 */
export function formatDateOnly(value: string | Date | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  if (!Number.isFinite(d.getTime())) return null
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${String(d.getUTCDate()).padStart(2, '0')} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * `formatDateOnly` with the em-dash convention applied, so call sites that
 * render straight into JSX or a template stay total.
 */
export function formatDateOnlyOr(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  return formatDateOnly(value) ?? fallback
}

/**
 * Parse a stored date as the calendar day it names.
 *
 * A bare 'YYYY-MM-DD' handed to `new Date()` is read as UTC midnight, so west
 * of UTC it lands on the previous local evening: a task due today reads as
 * overdue, an invoice due today goes past due, a Jan 1 payment drops out of
 * the year. Date-only strings are built in local time here; anything with a
 * time component is parsed as the instant it is.
 */
export function parseDateOnly(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/.exec(value)
  const hasTime = /[T ]\d{2}:\d{2}/.test(value)
  if (m && !hasTime) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Today's (or the given instant's) calendar day in local time, as 'YYYY-MM-DD'. */
export function localDateString(d: Date = new Date()): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Local midnight of the given date. */
export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * A count and its noun, agreeing: plural(1, 'deal') is "1 deal".
 *
 * Written out because the app had ten places printing "1 team members",
 * "1 tasks due today", "1 prospects haven't been reached" - the last two
 * going out as notifications.
 */
export function plural(n: number, one: string, many: string = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * Whole days from now until a date. Negative once the date has passed.
 *
 * The fraction is dropped in both directions, which is Math.trunc and not
 * Math.floor. Math.floor rounds a negative away from zero, so a deadline
 * 74.9 days gone reads as 75 days late - a day more than has actually
 * elapsed. Truncating gives 74 late, and 3 remaining for something 3.9 days
 * off: in both cases the number of whole days that have or have not passed.
 *
 * It lives here because every screen was working it out for itself and two of
 * them disagreed: Signals floored the signed delta and said a filing was 75
 * days late while the compliance register ceiled it and said 74, for the same
 * filing at the same moment.
 */
export function wholeDaysUntil(value: string | Date | null | undefined): number | null {
  if (!value) return null
  const t = new Date(value).getTime()
  if (!Number.isFinite(t)) return null
  const days = Math.trunc((t - Date.now()) / 86_400_000)
  // Math.trunc hands back -0 for anything inside the current day on the past
  // side. It compares equal to 0 but prints as "-0", so it is normalised here
  // rather than at each call site.
  return days === 0 ? 0 : days
}
